import { useState, type ReactNode } from "react";
import {
  useAddCatalogModel,
  useProviderCatalog,
  useRemoveCatalogModel,
  useSetDefaultModel,
  useUpdateCatalogModel,
  type ModelOut,
} from "@/api/queries";
import { Button, IconButton } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { Spinner } from "@/components/ui/Spinner";
import { HideIcon, NoteIcon, PlusIcon, StarIcon, TrashIcon, ViewIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The model catalog for one provider (PRD FR-3.3, FR-3.4, FR-3.5).
 *
 * Every row is an administrator's decision laid over a discovery fact: hide it,
 * rename it, correct its window, price it, move it to the other prompt variant,
 * or write down what it is actually like here. Discovery keeps refreshing the
 * facts underneath and never touches the decisions.
 *
 * The prompt-variant column is here because M3 made it consequential: the
 * compact variant shipped with no artifact vocabulary in it, so every local
 * deployment produced tables and never a chart. A 32B local model with a 128K
 * window has no reason to be on the short prompt, and this is where someone can
 * see that and change it.
 */

const WINDOW_HINT: Record<string, string> = {
  provider: "reported by the provider",
  dataset: "from the maintained model list",
  administrator: "set by you",
  unknown: "not published by this provider",
};

function compactTokens(value: number): string {
  if (value >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(value);
}

function Cell({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 align-top", className)}>{children}</td>;
}

/** A number an admin edits in place. Blank means "no opinion", never zero. */
function NumberInput({
  value,
  placeholder,
  label,
  onCommit,
}: {
  value: number | null | undefined;
  placeholder?: string;
  label: string;
  onCommit: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  return (
    <input
      type="number"
      min={0}
      step="any"
      aria-label={label}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        const next = draft.trim() === "" ? null : Number(draft);
        if (next !== (value ?? null) && !Number.isNaN(next)) onCommit(next);
      }}
      className={cn(
        "control-sm rounded-inner bg-card text-ink type-support w-24 px-2 text-end tabular-nums",
        "border-border hover:border-border-emphasized focus:border-accent border",
        "motion-fast outline-none",
      )}
    />
  );
}

function TextInput({
  value,
  placeholder,
  label,
  onCommit,
}: {
  value: string;
  placeholder?: string;
  label: string;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <input
      aria-label={label}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft !== value) onCommit(draft);
      }}
      className={cn(
        "control-sm rounded-inner bg-card text-ink type-support w-full px-2",
        "border-border hover:border-border-emphasized focus:border-accent border",
        "placeholder:text-ink-disabled motion-fast outline-none",
      )}
    />
  );
}

function ModelRow({
  model,
  providerId,
  isDefault,
}: {
  model: ModelOut;
  providerId: string;
  isDefault: boolean;
}) {
  const update = useUpdateCatalogModel(providerId);
  const remove = useRemoveCatalogModel(providerId);
  const setDefault = useSetDefaultModel();
  const [notesOpen, setNotesOpen] = useState(Boolean(model.notes));

  // The two `clear_*` flags carry defaults on the server, which the generated
  // client turns into required fields; spelling them out here keeps every call
  // site to the one field it means to change.
  const patch = (body: Record<string, unknown>) =>
    update.mutate({
      name: model.name,
      clear_prices: false,
      clear_window_override: false,
      ...body,
    });

  return (
    <>
      <tr className={cn("border-border border-t", model.hidden && "opacity-55")}>
        <Cell className="min-w-56">
          <div className="flex flex-col gap-1">
            <span className="type-code text-ink break-words">{model.name}</span>
            <div className="flex flex-wrap items-center gap-1">
              <Chip tone="neutral">{model.source}</Chip>
              {model.local ? <Chip tone="accent">local</Chip> : null}
              {isDefault ? <Chip tone="success">default</Chip> : null}
              {/* A measurement, not a decision (EVAL-2). Unmeasured shows
                  nothing at all — never zeros. */}
              {model.eval_summary ? (
                <span
                  title={`Measured ${String(model.eval_summary.measured_at ?? "").slice(0, 10)} by “${String(model.eval_summary.suite_name ?? "")}”`}
                >
                  <Chip tone="accent">
                    measured {String(model.eval_summary.passed ?? 0)}/
                    {String(model.eval_summary.cases ?? 0)}
                  </Chip>
                </span>
              ) : null}
              {/* Its own slot (MSE-4): neither measurement erases the other. */}
              {model.eval_summary_multi ? (
                <span
                  title={`Measured across ${((model.eval_summary_multi.sources as string[] | undefined) ?? []).join(" + ")} on ${String(model.eval_summary_multi.measured_at ?? "").slice(0, 10)} by “${String(model.eval_summary_multi.suite_name ?? "")}”`}
                >
                  <Chip tone="accent">
                    cross-source {String(model.eval_summary_multi.passed ?? 0)}/
                    {String(model.eval_summary_multi.cases ?? 0)}
                  </Chip>
                </span>
              ) : null}
            </div>
          </div>
        </Cell>

        <Cell className="min-w-40">
          <TextInput
            label={`Display name for ${model.name}`}
            value={model.display_name ?? ""}
            placeholder={model.name}
            onCommit={(value) => patch({ display_name: value })}
          />
        </Cell>

        <Cell className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            <NumberInput
              label={`Context window for ${model.name}`}
              value={model.context_window}
              placeholder="—"
              onCommit={(value) =>
                patch(
                  value === null
                    ? { clear_window_override: true }
                    : { context_window_override: value },
                )
              }
            />
            <span className="type-caption text-ink-disabled">
              {model.context_window ? compactTokens(model.context_window) : "unknown"}
            </span>
          </div>
          <p className="type-caption text-ink-disabled mt-1">
            {WINDOW_HINT[model.window_source] ?? model.window_source}
          </p>
        </Cell>

        <Cell className="whitespace-nowrap">
          <div className="flex items-center gap-1">
            <NumberInput
              label={`Input price for ${model.name}`}
              value={model.price.input_per_mtok}
              placeholder="in"
              onCommit={(value) => patch({ input_per_mtok: value ?? 0 })}
            />
            <NumberInput
              label={`Output price for ${model.name}`}
              value={model.price.output_per_mtok}
              placeholder="out"
              onCommit={(value) => patch({ output_per_mtok: value ?? 0 })}
            />
            <span className="type-caption text-ink-disabled">{model.price.currency}/M</span>
          </div>
          <p className="type-caption text-ink-disabled mt-1">
            {model.price.source || "no price — cost is not estimated"}
          </p>
        </Cell>

        <Cell>
          <select
            aria-label={`Prompt variant for ${model.name}`}
            value={model.prompt_variant}
            onChange={(event) => patch({ prompt_variant: event.target.value })}
            className={cn(
              "control-sm rounded-inner bg-card text-ink type-support px-2",
              "border-border hover:border-border-emphasized focus:border-accent border",
              "motion-fast outline-none",
            )}
          >
            <option value="full">full</option>
            <option value="compact">compact</option>
          </select>
        </Cell>

        <Cell>
          <div className="flex items-center justify-end gap-0.5">
            <IconButton
              label={isDefault ? "Already the deployment default" : "Make the deployment default"}
              onClick={() => setDefault.mutate(model.ref)}
              disabled={isDefault || model.hidden}
              className={cn("control-sm", isDefault && "text-success")}
            >
              <StarIcon className="size-4" />
            </IconButton>
            <IconButton
              label={model.hidden ? "Show in the picker" : "Hide from the picker"}
              onClick={() => patch({ hidden: !model.hidden })}
              className="control-sm"
            >
              {model.hidden ? <ViewIcon className="size-4" /> : <HideIcon className="size-4" />}
            </IconButton>
            <IconButton
              label={notesOpen ? "Hide notes" : "Notes on this model"}
              onClick={() => setNotesOpen((open) => !open)}
              className={cn("control-sm", model.notes && "text-accent")}
            >
              <NoteIcon className="size-4" />
            </IconButton>
            <IconButton
              label="Remove from the catalog"
              onClick={() => remove.mutate(model.name)}
              className="control-sm"
            >
              <TrashIcon className="size-4" />
            </IconButton>
          </div>
        </Cell>
      </tr>

      {notesOpen ? (
        <tr>
          <td colSpan={6} className="px-3 pb-3">
            <TextInput
              label={`Notes for ${model.name}`}
              value={model.notes}
              placeholder="What this model is like here — does it hold a binding scope across a follow-up, does it attach a chart rather than retyping rows?"
              onCommit={(value) => patch({ notes: value })}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function ModelCatalog({ providerId }: { providerId: string }) {
  const { data, isLoading } = useProviderCatalog(providerId);
  const add = useAddCatalogModel(providerId);
  const [manual, setManual] = useState("");

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }
  if (!data) return null;

  const hidden = data.models.filter((model) => model.hidden).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-h4 text-ink">Models</span>
        <Chip tone="neutral">{data.models.length} in the catalog</Chip>
        {hidden ? <Chip tone="warning">{hidden} hidden</Chip> : null}
      </div>

      {data.models.length === 0 ? (
        <p className="type-support text-ink-secondary">
          Nothing discovered yet. Check the provider above, or add a model by name.
        </p>
      ) : (
        // Six editable columns do not fit a laptop beside the sidebar, and
        // wrapping a model name mid-token makes it unreadable — so the
        // container scrolls rather than the columns collapsing.
        <div className="border-border rounded-container overflow-x-auto border">
          <table className="w-full min-w-[62rem] border-collapse">
            <thead>
              <tr className="bg-surface text-ink-secondary type-caption">
                <th className="px-3 py-2 text-start font-medium">Model</th>
                <th className="px-3 py-2 text-start font-medium">Shown as</th>
                <th className="px-3 py-2 text-start font-medium">Context window</th>
                <th className="px-3 py-2 text-start font-medium">Price per 1M tokens</th>
                <th className="px-3 py-2 text-start font-medium">Prompt</th>
                <th className="px-3 py-2 text-end font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.models.map((model) => (
                <ModelRow
                  key={model.ref}
                  model={model}
                  providerId={providerId}
                  isDefault={model.ref === data.default_ref}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          aria-label="Add a model by name"
          value={manual}
          placeholder="Add a model this provider does not list…"
          onChange={(event) => setManual(event.target.value)}
          className={cn(
            "control-md rounded-element bg-card text-ink type-support flex-1 px-3",
            "border-border hover:border-border-emphasized focus:border-accent border",
            "placeholder:text-ink-disabled motion-fast outline-none",
          )}
        />
        <Button
          size="sm"
          icon={<PlusIcon className="size-3.5" />}
          disabled={!manual.trim()}
          loading={add.isPending}
          onClick={() => add.mutate(manual.trim(), { onSuccess: () => setManual("") })}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
